/* Rallye du Pyla — composants des sections rdp-* */

const reduitMouvement = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
window.rdpPret = true;

/* Un lien dont l'adresse est « # » (onglet de menu qui ne sert qu'à ouvrir son sous-menu) ne navigue pas. */
document.addEventListener('click', (e) => {
  const lien = e.target.closest?.('a[href="#"], a[href$="/#"]');
  if (lien) e.preventDefault();
});

/* Entrées en scène : un élément [data-rdp-entree] (haut par défaut, « gauche », « droite », « zoom ») reçoit
   .est-entre quand il arrive à l'écran, une seule fois. Le décalage de départ et le délai sont dans le CSS. */
function entreesEnScene() {
  const elements = document.querySelectorAll('[data-rdp-entree]');
  if (!elements.length) return;
  if (reduitMouvement() || !('IntersectionObserver' in window)) { elements.forEach((el) => el.classList.add('est-entre')); return; }
  const obs = new IntersectionObserver((vues) => vues.forEach((v) => {
    if (!v.isIntersecting) return;
    v.target.classList.add('est-entre');
    obs.unobserve(v.target);
  }), { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
  elements.forEach((el) => obs.observe(el));
}
entreesEnScene();

/* Défilement de la page. Sur ordinateur (≥ 990 px), Horizon ne fait pas défiler la fenêtre mais
   le conteneur .page-wrapper : un écouteur posé sur window seul ne reçoit donc rien.
   surDefilement() écoute les deux, positionDefilement() lit la bonne position. */
const pageDefilante = () => (window.matchMedia('(min-width: 990px)').matches && document.querySelector('.page-wrapper')) || null;
const positionDefilement = () => { const c = pageDefilante(); return c ? c.scrollTop : window.scrollY; };
function surDefilement(fn) {
  window.addEventListener('scroll', fn, { passive: true });
  document.querySelector('.page-wrapper')?.addEventListener('scroll', fn, { passive: true });
}

/* Masque les rendez-vous passés si la page vient du cache. */
function masquerPasses(racine = document) {
  const maintenant = Date.now() / 1000;
  racine.querySelectorAll('[data-rdp-fin]').forEach((el) => {
    const fin = Number(el.dataset.rdpFin);
    if (fin && fin < maintenant) {
      (el.closest('.rdp-carrousel__element') || el).remove();
    }
  });
}

/* Carrousel horizontal à défilement natif : flèches, barre de progression, points. */
class RdpCarrousel extends HTMLElement {
  connectedCallback() {
    this.piste = this.querySelector('[data-rdp-piste]');
    if (!this.piste) return;
    this.barre = this.querySelector('[data-rdp-barre]');
    this.prec = this.querySelector('[data-rdp-prec]');
    this.suiv = this.querySelector('[data-rdp-suiv]');
    this.points = [...this.querySelectorAll('[data-rdp-point]')];
    this.vignette = this.querySelector('[data-rdp-vignette]');
    this.centre = this.hasAttribute('data-rdp-centre');

    this.prec?.addEventListener('click', () => this.aller(-1));
    this.suiv?.addEventListener('click', () => this.aller(1));
    this.points.forEach((p) => p.addEventListener('click', () => this.allerA(Number(p.dataset.rdpPoint))));
    this.piste.addEventListener('scroll', () => this.maj(), { passive: true });
    this.piste.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.aller(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.aller(-1); }
    });
    this.observateur = new ResizeObserver(() => this.maj());
    this.observateur.observe(this.piste);
    if (this.centre) requestAnimationFrame(() => this.allerA(0, 'instant'));
    this.maj();
    this.defilementAuto();
  }

  disconnectedCallback() {
    this.observateur?.disconnect();
    this.observateurVue?.disconnect();
    clearInterval(this.minuterie);
  }

  /* Défilement automatique (data-rdp-auto = délai en ms) : avance d'un élément, revient au début une fois au bout.
     Suspendu quand le focus clavier est dedans, hors de l'écran ou onglet masqué (pas au survol : le bouton pause est là pour ça).
     Le bouton [data-rdp-lecture] le met en pause ou le relance ; pas de départ automatique si l'utilisateur
     a demandé moins d'animations. */
  defilementAuto() {
    const delai = Number(this.dataset.rdpAuto);
    const bouton = this.querySelector('[data-rdp-lecture]');
    if (!delai) { bouton?.remove(); return; }
    let enPause = reduitMouvement();
    let survol = false;
    let visible = false;
    const afficher = () => {
      this.toggleAttribute('data-rdp-pause', enPause);
      if (bouton) bouton.setAttribute('aria-label', enPause ? bouton.dataset.libelleLecture : bouton.dataset.libellePause);
    };
    const avancer = () => {
      if (enPause || survol || !visible || document.hidden) return;
      const max = this.piste.scrollWidth - this.piste.clientWidth;
      if (max <= 2) return;
      if (this.piste.scrollLeft >= max - 2) this.allerA(0); else this.aller(1);
    };
    const relancer = () => { clearInterval(this.minuterie); this.minuterie = setInterval(avancer, delai); };
    bouton?.addEventListener('click', () => { enPause = !enPause; afficher(); relancer(); });
    /* une action manuelle (flèches, glissé) repart pour un délai complet */
    [this.prec, this.suiv].forEach((b) => b?.addEventListener('click', relancer));
    this.piste.addEventListener('pointerdown', relancer);
    /* au clavier, le défilement attend tant que le focus est dans le carrousel (hors bouton pause) */
    this.addEventListener('focusin', (e) => { if (e.target !== bouton && e.target.matches(':focus-visible')) survol = true; });
    this.addEventListener('focusout', () => { survol = false; });
    this.observateurVue = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) relancer(); }, { threshold: 0.35 });
    this.observateurVue.observe(this);
    afficher();
    relancer();
  }

  get elements() {
    return [...this.piste.children];
  }

  indexCourant() {
    const reperePiste = this.piste.getBoundingClientRect();
    const repere = this.centre ? reperePiste.left + reperePiste.width / 2 : reperePiste.left + parseFloat(getComputedStyle(this.piste).paddingLeft || 0);
    let meilleur = 0;
    let ecart = Infinity;
    this.elements.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const pos = this.centre ? r.left + r.width / 2 : r.left;
      const d = Math.abs(pos - repere);
      if (d < ecart) { ecart = d; meilleur = i; }
    });
    return meilleur;
  }

  allerA(i, comportement) {
    const el = this.elements[Math.max(0, Math.min(i, this.elements.length - 1))];
    if (!el) return;
    const reperePiste = this.piste.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    let cible;
    if (this.centre) {
      cible = this.piste.scrollLeft + (r.left + r.width / 2) - (reperePiste.left + reperePiste.width / 2);
    } else {
      cible = this.piste.scrollLeft + r.left - reperePiste.left - parseFloat(getComputedStyle(this.piste).paddingLeft || 0);
    }
    this.piste.scrollTo({ left: cible, behavior: comportement || (reduitMouvement() ? 'instant' : 'smooth') });
  }

  placerVignette(i) {
    if (!this.vignette || !this.points[i]) return;
    const point = this.points[i];
    this.vignette.style.setProperty('--rdp-vignette-x', `${point.offsetLeft + point.offsetWidth / 2}px`);
    this.vignette.querySelectorAll('[data-rdp-vignette-image]').forEach((img) => {
      img.classList.toggle('est-visible', Number(img.dataset.rdpVignetteImage) === i);
    });
  }

  aller(sens) {
    this.allerA(this.indexCourant() + sens);
  }

  maj() {
    const max = this.piste.scrollWidth - this.piste.clientWidth;
    const ratio = max > 0 ? this.piste.scrollLeft / max : 0;
    if (this.barre) {
      const visible = this.piste.scrollWidth > 0 ? this.piste.clientWidth / this.piste.scrollWidth : 1;
      this.barre.style.width = `${Math.max(visible, 0.08) * 100}%`;
      this.barre.style.transform = `translateX(${ratio * (1 / Math.max(visible, 0.08) - 1) * 100}%)`;
    }
    const defilable = max > 2;
    this.toggleAttribute('data-rdp-statique', !defilable && !this.centre);
    if (this.prec) this.prec.disabled = !this.centre && this.piste.scrollLeft <= 2;
    if (this.suiv) this.suiv.disabled = !this.centre && this.piste.scrollLeft >= max - 2;
    if (this.points.length || this.centre || this.hasAttribute('data-rdp-actif')) {
      const i = this.indexCourant();
      this.points.forEach((p, n) => {
        p.toggleAttribute('aria-current', n === i);
        /* Les points éloignés du point actif rapetissent, comme sur la maquette. */
        const distance = Math.abs(n - i);
        p.dataset.rdpDistance = distance >= 5 ? 'loin' : distance >= 4 ? 'moyen' : '';
      });
      this.placerVignette(i);
      this.elements.forEach((el, n) => el.classList.toggle('est-courant', n === i));
      if (this.prec) this.prec.disabled = i === 0;
      if (this.suiv) this.suiv.disabled = i === this.elements.length - 1;
    }
  }
}

/* Onglets accessibles (flèches du clavier), titres synchronisés. */
class RdpOnglets extends HTMLElement {
  connectedCallback() {
    this.onglets = [...this.querySelectorAll(':scope [role="tab"]')];
    this.onglets.forEach((o, i) => {
      o.addEventListener('click', () => {
        this.choisir(i);
        /* l'adresse garde l'onglet (lien partageable) */
        if (o.dataset.rdpOnglet) history.replaceState(null, '', `#${encodeURIComponent(o.dataset.rdpOnglet)}`);
      });
      o.addEventListener('keydown', (e) => {
        const avant = ['ArrowUp', 'ArrowLeft'].includes(e.key);
        const apres = ['ArrowDown', 'ArrowRight'].includes(e.key);
        if (!avant && !apres) return;
        e.preventDefault();
        const n = (i + (apres ? 1 : -1) + this.onglets.length) % this.onglets.length;
        this.choisir(n);
        this.onglets[n].focus();
      });
    });
    /* Onglet demandé dans l'adresse : /collections/billetterie#le-gt-tour */
    if (this.onglets.some((o) => o.dataset.rdpOnglet)) {
      const depuisAdresse = () => {
        const cle = decodeURIComponent(window.location.hash.slice(1));
        const i = cle ? this.onglets.findIndex((o) => o.dataset.rdpOnglet === cle) : -1;
        if (i < 0) return;
        this.choisir(i);
        /* sur mobile, la rangée d'onglets défile : on amène l'onglet choisi dans le champ */
        const o = this.onglets[i];
        const rangee = o.parentElement;
        if (rangee && rangee.scrollWidth > rangee.clientWidth) rangee.scrollLeft = Math.max(0, o.offsetLeft - 16);
      };
      depuisAdresse();
      window.addEventListener('hashchange', depuisAdresse);
    }
  }

  choisir(i) {
    this.onglets.forEach((o, n) => {
      const actif = n === i;
      o.setAttribute('aria-selected', actif ? 'true' : 'false');
      o.tabIndex = actif ? 0 : -1;
      const panneau = document.getElementById(o.getAttribute('aria-controls'));
      if (panneau) panneau.hidden = !actif;
    });
    /* Couleurs de l'événement choisi (fond et texte de la section) */
    const onglet = this.onglets[i];
    const racine = this.closest('[data-rdp-theme]');
    if (racine && onglet?.dataset.rdpFond) racine.style.setProperty('--evt-fond', onglet.dataset.rdpFond);
    if (racine && onglet?.dataset.rdpTexte) racine.style.setProperty('--evt-texte', onglet.dataset.rdpTexte);
    this.querySelectorAll('[data-rdp-titre]').forEach((t) => {
      t.hidden = Number(t.dataset.rdpTitre) !== i;
    });
    this.querySelectorAll('rdp-carrousel').forEach((c) => c.maj?.());
  }
}

/* Diaporama pas à pas (carte « Prochains rendez-vous »). */
class RdpDiaporama extends HTMLElement {
  connectedCallback() {
    this.diapos = [...this.querySelectorAll('[data-rdp-diapo]')];
    this.i = 0;
    this.montrer(0);
    const prec = this.querySelector('[data-rdp-prec]');
    const suiv = this.querySelector('[data-rdp-suiv]');
    if (this.diapos.length < 2) {
      prec?.setAttribute('hidden', '');
      suiv?.setAttribute('hidden', '');
    }
    prec?.addEventListener('click', () => this.montrer(this.i - 1));
    suiv?.addEventListener('click', () => this.montrer(this.i + 1));
  }

  montrer(i) {
    this.diapos = this.diapos.filter((d) => d.isConnected);
    const n = this.diapos.length;
    if (!n) return;
    this.i = (i + n) % n;
    this.diapos.forEach((d, k) => { d.hidden = k !== this.i; });
  }
}

/* Barre d'événement : le menu « Infos » liste les sections de la page qui ont un libellé,
   puis les pages de l'événement. Les mêmes ancres alimentent le bloc de fin. */
class RdpSousNav extends HTMLElement {
  connectedCallback() {
    const sections = [...document.querySelectorAll('[data-rdp-ancre]')].filter((s) => s.dataset.rdpAncre && s.id);
    const liens = sections.map((s) => ({ texte: s.dataset.rdpAncre, href: `#${s.id}` }));
    const ajouter = (liste, enTete) => {
      if (!liste) return;
      const fragment = document.createDocumentFragment();
      const dejaLa = new Set([...liste.querySelectorAll('a')].map((a) => a.textContent.trim().toLowerCase()));
      liens.forEach((l) => {
        /* une page de l'événement du même nom l'emporte sur l'ancre */
        if (dejaLa.has(l.texte.trim().toLowerCase())) return;
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = l.href;
        a.textContent = l.texte;
        li.append(a);
        fragment.append(li);
      });
      if (enTete) liste.prepend(fragment); else liste.prepend(fragment);
    };
    /* Un menu choisi dans l'éditeur remplace la liste automatique. */
    if (!this.hasAttribute('data-rdp-menu-fixe')) ajouter(this.querySelector('[data-rdp-infos-liste]'), true);
    document.querySelectorAll('[data-rdp-liens-ancres]').forEach((l) => ajouter(l));

    /* Un lien du menu (barre ou pied de l'événement) vers une ancre absente de cette page (section retirée, FAQ sans réponse, boutique fermée…) ne s'affiche pas. */
    const ici = window.location.pathname.replace(/\/$/, '');
    [this, ...document.querySelectorAll('[data-rdp-liens-ancres], .rdp-evt-pied__liens')].forEach((zone) => zone.querySelectorAll('a[href*="#"]').forEach((lien) => {
      const u = new URL(lien.href, window.location.href);
      if (u.hash.length < 2 || u.pathname.replace(/\/$/, '') !== ici) return;
      let cible = null;
      try { cible = document.getElementById(decodeURIComponent(u.hash.slice(1))); } catch (e) { return; }
      if (!cible) (lien.closest('li') || lien).hidden = true;
    }));

    const details = this.querySelector('[data-rdp-infos]');
    if (details) {
      details.addEventListener('click', (e) => { if (e.target.closest('a') || e.target.closest('[data-rdp-infos-fermer]')) details.open = false; });
      /* Panneau plein écran sur mobile : la page ne défile plus derrière */
      details.addEventListener('toggle', () => document.documentElement.classList.toggle('rdp-infos-ouvert', details.open && window.innerWidth < 990));
      document.addEventListener('click', (e) => { if (!details.contains(e.target)) details.open = false; });
      details.addEventListener('keydown', (e) => { if (e.key === 'Escape') { details.open = false; details.querySelector('summary').focus(); } });
    }
  }
}

/* Vidéo YouTube / Vimeo dans une fenêtre, chargée seulement à l'ouverture */
class RdpVideo extends HTMLElement {
  connectedCallback() {
    this.fenetre = this.querySelector('[data-rdp-video-fenetre]');
    this.cadre = this.querySelector('[data-rdp-video-cadre]');
    this.querySelector('[data-rdp-video-ouvrir]')?.addEventListener('click', () => this.ouvrir());
    this.querySelector('[data-rdp-video-fermer]')?.addEventListener('click', () => this.fenetre.close());
    /* à la fermeture, la vidéo s'arrête et le lecteur est retiré */
    this.fenetre?.addEventListener('close', () => { this.cadre.querySelector('video')?.pause(); this.cadre.replaceChildren(); });
    /* un clic à côté de la vidéo (fond de la visionneuse) referme */
    this.fenetre?.addEventListener('click', (e) => { if (e.target === this.fenetre || e.target.matches('[data-rdp-video-scene]')) this.fenetre.close(); });
  }

  /* adresse d'intégration pour YouTube et Vimeo */
  adresse() {
    const url = this.dataset.url || '';
    const yt = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
    if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0`;
    const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1&dnt=1`;
    return null;
  }

  /* lecteur à afficher : fichier vidéo Shopify (modèle fourni par le thème), YouTube / Vimeo, ou lien direct vers un fichier vidéo */
  lecteur() {
    const titre = this.querySelector('[data-rdp-video-ouvrir]')?.textContent.trim() || 'Vidéo';
    const modele = this.querySelector('template[data-rdp-video-fichier]');
    const fichier = modele?.content.querySelector('video');
    if (fichier) {
      const video = fichier.cloneNode(true);
      video.autoplay = true;
      video.setAttribute('aria-label', titre);
      return video;
    }
    const url = this.dataset.url || '';
    const src = this.adresse();
    if (src) {
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = titre;
      return iframe;
    }
    if (/\.(mp4|m4v|webm|mov)(\?|#|$)/i.test(url)) {
      const video = document.createElement('video');
      video.src = url;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('aria-label', titre);
      return video;
    }
    return null;
  }

  ouvrir() {
    const lecteur = this.lecteur();
    if (!lecteur) { if (this.dataset.url) window.open(this.dataset.url, '_blank', 'noopener'); return; }
    this.cadre.replaceChildren(lecteur);
    this.fenetre.showModal();
    lecteur.play?.()?.catch(() => {});
  }
}

masquerPasses();

customElements.get('rdp-carrousel') || customElements.define('rdp-carrousel', RdpCarrousel);
customElements.get('rdp-onglets') || customElements.define('rdp-onglets', RdpOnglets);
customElements.get('rdp-diaporama') || customElements.define('rdp-diaporama', RdpDiaporama);
customElements.get('rdp-sous-nav') || customElements.define('rdp-sous-nav', RdpSousNav);
customElements.get('rdp-video') || customElements.define('rdp-video', RdpVideo);
document.addEventListener('shopify:section:load', (e) => masquerPasses(e.target));

/* Texte révélé mot à mot quand il entre à l'écran (titres de la page événement).
   Les mots glissent depuis le bas de leur ligne, avec un léger décalage. */
function reveler(racine = document) {
  const cibles = racine.querySelectorAll('[data-rdp-revele]:not([data-rdp-revele-pret])');
  if (!cibles.length) return;
  cibles.forEach((el) => {
    el.setAttribute('data-rdp-revele-pret', '');
    if (reduitMouvement()) return;
    let n = 0;
    const decouper = (noeud) => {
      [...noeud.childNodes].forEach((enfant) => {
        if (enfant.nodeType === Node.TEXT_NODE) {
          const fragment = document.createDocumentFragment();
          enfant.textContent.split(/(\s+)/).forEach((morceau) => {
            if (!morceau) return;
            if (/^\s+$/.test(morceau)) { fragment.append(morceau); return; }
            const mot = document.createElement('span');
            mot.className = 'rdp-revele__mot';
            const interieur = document.createElement('span');
            interieur.textContent = morceau;
            interieur.style.transitionDelay = `${Math.min(n++ * 45, 900)}ms`;
            mot.append(interieur);
            fragment.append(mot);
          });
          enfant.replaceWith(fragment);
        } else if (enfant.nodeType === Node.ELEMENT_NODE && enfant.tagName !== 'BR') {
          decouper(enfant);
        }
      });
    };
    decouper(el);
    el.classList.add('rdp-revele');
  });
  if (reduitMouvement()) return;
  const obs = new IntersectionObserver((entrees) => {
    entrees.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('est-revele');
      obs.unobserve(e.target);
    });
  }, { threshold: 0.25 });
  racine.querySelectorAll('.rdp-revele:not(.est-revele)').forEach((el) => obs.observe(el));
}

/* Photos qui suivent le curseur dans une section (souris seulement). */
function traineePhotos(racine = document) {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches || reduitMouvement()) return;
  racine.querySelectorAll('template[data-rdp-survol-photos]').forEach((modele) => {
    const section = modele.parentElement;
    if (!section || section.dataset.rdpTrainee) return;
    section.dataset.rdpTrainee = '';
    const sources = [...modele.content.querySelectorAll('img')].map((img) => img.getAttribute('src'));
    if (!sources.length) return;
    let i = 0;
    let dernier = null;
    section.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      /* photos plus espacées, et jamais plus de trois à l'écran à la fois */
      if (dernier && Math.hypot(e.clientX - dernier.x, e.clientY - dernier.y) < 200) return;
      dernier = { x: e.clientX, y: e.clientY };
      const r = section.getBoundingClientRect();
      const img = document.createElement('img');
      img.src = sources[i++ % sources.length];
      img.alt = '';
      img.className = 'rdp-trainee';
      img.style.left = `${e.clientX - r.left}px`;
      img.style.top = `${e.clientY - r.top}px`;
      img.style.setProperty('--rdp-rotation', `${(Math.random() * 14 - 7).toFixed(1)}deg`);
      section.append(img);
      img.addEventListener('animationend', () => img.remove());
      const affichees = section.querySelectorAll(':scope > .rdp-trainee');
      if (affichees.length > 3) affichees[0].remove();
    });
  });
}

reveler();
traineePhotos();
document.addEventListener('shopify:section:load', (e) => { reveler(e.target); traineePhotos(e.target); });

/* Parallaxe verticale dans les arches : le contenu glisse de bas en haut, un peu moins vite
   que la page, au fil du défilement (le carrousel ne le fait plus glisser sur le côté). */
function parallaxe() {
  if (reduitMouvement()) return;
  let prevu = false;
  const calculer = () => {
    prevu = false;
    const h = window.innerHeight;
    document.querySelectorAll('[data-rdp-parallaxe]').forEach((el) => {
      const r = el.parentElement.getBoundingClientRect();
      if (r.bottom < 0 || r.top > h) return;
      const vertical = (r.top + r.height / 2) / h - 0.5;
      /* photo : ±10 % de la hauteur de l'arche au plus (elle dépasse de 12 %) ; texte : la moitié */
      const amplitude = r.height * (el.classList.contains('rdp-arche__cadre') ? 0.12 : 0.06);
      el.style.setProperty('--rdp-py', `${(-vertical * amplitude).toFixed(1)}px`);
    });
  };
  const demander = () => { if (!prevu) { prevu = true; requestAnimationFrame(calculer); } };
  surDefilement(demander);
  window.addEventListener('resize', demander);
  document.querySelectorAll('[data-rdp-piste]').forEach((p) => p.addEventListener('scroll', demander, { passive: true }));
  calculer();
}
parallaxe();

/* Voiture de la bande illustrée : elle regarde vers la gauche, donc elle avance vers la gauche quand on descend.
   Elle part du bord droit de l'écran quand la bande entre en bas, et touche le bord gauche quand la bande sort en haut,
   quelle que soit la largeur de l'écran (tout est mesuré en pixels sur la bande). */
function voitureAuDefilement() {
  const voitures = [...document.querySelectorAll('[data-rdp-voiture]')];
  if (!voitures.length || reduitMouvement()) return;
  let prevu = false;
  const maj = () => {
    prevu = false;
    const h = window.innerHeight;
    voitures.forEach((v) => {
      const bande = v.closest('.rdp-bande, .rdp-pied') || v.parentElement;
      const r = bande.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (h - r.top) / (h + r.height)));
      const depart = r.width - v.offsetWidth - v.offsetLeft;
      const arrivee = -v.offsetLeft;
      v.style.transform = `translateX(${(depart + (arrivee - depart) * p).toFixed(1)}px)`;
    });
  };
  const demander = () => { if (!prevu) { prevu = true; requestAnimationFrame(maj); } };
  surDefilement(demander);
  window.addEventListener('resize', demander);
  window.addEventListener('load', demander);
  maj();
}
voitureAuDefilement();

/* Bloc dépliable (jours du programme) : un bouton ouvre ou ferme la liste qu'il contrôle. */
class RdpDepliant extends HTMLElement {
  connectedCallback() {
    this.bouton = this.querySelector('[data-rdp-depliant-bouton]');
    this.bouton?.addEventListener('click', () => {
      const ouvert = this.bouton.getAttribute('aria-expanded') === 'true';
      this.bouton.setAttribute('aria-expanded', ouvert ? 'false' : 'true');
      const cible = document.getElementById(this.bouton.getAttribute('aria-controls'));
      if (cible) cible.hidden = ouvert;
    });
  }
}
customElements.get('rdp-depliant') || customElements.define('rdp-depliant', RdpDepliant);

/* Photos flottantes (page Animations) : léger décalage vertical au défilement. */
function photosFlottantes() {
  const photos = [...document.querySelectorAll('[data-rdp-flottant]')];
  if (!photos.length || reduitMouvement()) return;
  let prevu = false;
  const maj = () => {
    prevu = false;
    const h = window.innerHeight;
    photos.forEach((el) => {
      const r = el.parentElement.getBoundingClientRect();
      if (r.bottom < -200 || r.top > h + 200) return;
      const centre = (r.top + r.height / 2 - h / 2) / h;
      el.style.setProperty('--rdp-flotte', `${(-centre * Number(el.dataset.rdpFlottant) * 400).toFixed(1)}px`);
    });
  };
  const demander = () => { if (!prevu) { prevu = true; requestAnimationFrame(maj); } };
  surDefilement(demander);
  window.addEventListener('resize', demander);
  maj();
}
photosFlottantes();

/* En-tête dédié d'un événement : panneau du menu, panier latéral, hauteur pour les barres collantes. */
function enteteEvenement() {
  const entete = document.querySelector('[data-rdp-entete]');
  if (!entete) return;
  /* L'en-tête dédié défile avec la page : seule la barre de l'événement reste collée en haut. */
  const barre = document.querySelector('.rdp-evt-entete');
  const hauteur = () => document.body.style.setProperty('--header-height', `${barre ? barre.offsetHeight : 0}px`);
  hauteur();
  if (barre) new ResizeObserver(hauteur).observe(barre);

  const menu = document.querySelector('[data-rdp-menu-lateral]');
  const ouvrir = entete.querySelector('[data-rdp-menu-ouvrir]');
  if (menu && ouvrir) {
    ouvrir.addEventListener('click', () => { menu.showModal(); ouvrir.setAttribute('aria-expanded', 'true'); });
    menu.addEventListener('close', () => ouvrir.setAttribute('aria-expanded', 'false'));
    menu.querySelector('[data-rdp-menu-fermer]')?.addEventListener('click', () => menu.close());
    /* un clic sur le voile (hors du panneau) ferme */
    menu.addEventListener('click', (e) => { if (!e.target.closest('.rdp-menu-lateral__panneau')) menu.close(); });
  }

  entete.querySelector('[data-rdp-panier-ouvrir]')?.addEventListener('click', () => {
    const tiroir = document.getElementById('cart-drawer');
    if (tiroir?.toggle) tiroir.toggle(); else window.location.href = window.Theme?.routes?.cart_url || '/cart';
  });

  /* Menus déroulants <details> : fermeture au clic extérieur et à Échap */
  document.querySelectorAll('details[data-rdp-fermable]').forEach((d) => {
    document.addEventListener('click', (e) => { if (!d.contains(e.target)) d.open = false; });
    d.addEventListener('keydown', (e) => { if (e.key === 'Escape') d.open = false; });
  });
}
enteteEvenement();

/* Colonne collante plus haute que l'écran (fiche billet) : elle colle par le bas,
   pour qu'on puisse lire tout son contenu en faisant défiler la galerie. */
function colonnesCollantes() {
  const colonnes = document.querySelectorAll('.rdp-fiche-produit__collant');
  if (!colonnes.length) return;
  const maj = () => colonnes.forEach((el) => {
    const entete = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 80;
    const haut = Math.min(entete + 24, window.innerHeight - el.offsetHeight - 24);
    el.style.setProperty('--rdp-collant-haut', `${haut}px`);
  });
  maj();
  window.addEventListener('resize', maj);
  colonnes.forEach((el) => new ResizeObserver(maj).observe(el));
}
colonnesCollantes();

/* Vignettes de la galerie (fiche billet, édition d'un événement) : celle de la photo à l'écran est pleine, les autres atténuées */
function vignettesGalerie() {
  document.querySelectorAll('.rdp-fiche-produit__galerie').forEach((galerie) => {
    const liens = [...galerie.querySelectorAll('.rdp-fiche-produit__vignettes a')];
    if (liens.length < 2) return;
    const activer = (lien) => liens.forEach((l) => l.toggleAttribute('aria-current', l === lien));
    const parCible = new Map(liens.map((l) => [l.getAttribute('href').slice(1), l]));
    const visibles = new Map();
    const io = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => visibles.set(e.target.id, e.intersectionRatio));
      let meilleur = null, ratio = 0;
      visibles.forEach((r, id) => { if (r > ratio) { ratio = r; meilleur = id; } });
      if (meilleur && parCible.get(meilleur)) activer(parCible.get(meilleur));
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    parCible.forEach((l, id) => { const m = document.getElementById(id); if (m) io.observe(m); });
    liens.forEach((l) => l.addEventListener('click', () => activer(l)));
  });
}
vignettesGalerie();

/* Pied de page : la couleur derrière la silhouette reprend le fond de la dernière section de la page
   (sauf si un « Bloc de fin » d'événement l'impose). */
function couleurSousSilhouette() {
  const pied = document.querySelector('.rdp-pied');
  if (!pied || document.querySelector('.rdp-evt-pied') || getComputedStyle(document.body).getPropertyValue('--rdp-pied-haut').trim()) return;
  const sections = [...document.querySelectorAll('#MainContent > .shopify-section')];
  for (let i = sections.length - 1; i >= 0; i--) {
    const el = sections[i].querySelector('section, div') || sections[i];
    const fond = getComputedStyle(el).backgroundColor;
    if (fond && fond !== 'rgba(0, 0, 0, 0)' && fond !== 'transparent') {
      pied.style.setProperty('--rdp-pied-haut', fond);
      break;
    }
  }
}
couleurSousSilhouette();

/* Couverture d'un événement en plein écran : on mesure ce qui la précède dans la page (en-tête, barre),
   le CSS en déduit sa hauteur pour que l'ensemble fasse exactement la hauteur de l'écran. */
function heroPleinEcran() {
  /* couverture des Chapeaux de Roues, des autres pages d'événement (format GT Tour, récap d'édition) et de l'accueil */
  const hero = document.querySelector('.rdp-evt-hero') || document.querySelector('.rdp-evt-couverture') || document.querySelector('.rdp-hero');
  if (!hero) return;
  /* la section qui suit, si c'est le bandeau défilant / le damier, fait partie du premier écran */
  const suivante = hero.closest('.shopify-section')?.nextElementSibling;
  const bandeau = hero.matches('.rdp-evt-hero') && suivante?.querySelector(':scope > .rdp-damier-bloc') ? suivante : null;
  const maj = () => {
    const dessus = hero.getBoundingClientRect().top + positionDefilement();
    hero.style.setProperty('--rdp-hero-dessus', `${Math.max(0, Math.round(dessus))}px`);
    hero.style.setProperty('--rdp-hero-dessous', `${bandeau ? Math.round(bandeau.getBoundingClientRect().height) : 0}px`);
  };
  maj();
  window.addEventListener('resize', maj);
  window.addEventListener('load', maj);
}
heroPleinEcran();

/* En-têtes collants — l'en-tête du site et la barre d'un événement — même comportement partout :
   ils glissent hors de l'écran quand on descend (une fois le haut de page dépassé) et reviennent dès qu'on remonte. */
function entetesAuDefilement() {
  const barre = document.querySelector('.rdp-evt-entete');
  const site = document.querySelector('#header-component[sticky]')?.closest('.header-section');
  /* en-tête dédié d'un événement (Rallye du Pyla × événement) : même comportement, sa hauteur cale la barre dessous */
  const dedie = document.querySelector('[data-rdp-entete]')?.closest('.shopify-section');
  if (dedie) {
    const hauteur = () => document.documentElement.style.setProperty('--rdp-entete-h', `${dedie.offsetHeight}px`);
    hauteur();
    window.addEventListener('resize', hauteur);
  }
  const cibles = [barre, site, dedie].filter(Boolean);
  if (!cibles.length) return;
  let dernier = positionDefilement();
  let prevu = false;
  const maj = () => {
    prevu = false;
    const y = positionDefilement();
    const ecart = y - dernier;
    if (Math.abs(ecart) < 6) return;
    const hautDePage = (barre?.offsetHeight || 0) + (site?.offsetHeight || 0) + (document.querySelector('[data-rdp-entete]')?.offsetHeight || 0) + 120;
    /* un menu ouvert (Infos, sous-menu, menu mobile) garde l'en-tête en place */
    const occupe = Boolean(barre?.querySelector('[data-rdp-infos][open]') || site?.querySelector('header-menu[aria-expanded="true"], details[open]') || dedie?.querySelector('details[open]'));
    const cacher = ecart > 0 && y > hautDePage && !occupe;
    cibles.forEach((c) => c.classList.toggle('est-cachee', cacher));
    dernier = y;
  };
  surDefilement(() => { if (!prevu) { prevu = true; requestAnimationFrame(maj); } });
}
entetesAuDefilement();

/* Contact : la carte sort de l'enveloppe quand la section arrive à l'écran. */
function enveloppes() {
  if (reduitMouvement() || !('IntersectionObserver' in window)) return;
  const obs = new IntersectionObserver((entrees) => {
    entrees.forEach((e) => {
      if (!e.isIntersecting) return;
      obs.unobserve(e.target);
      /* la carte ne sort qu'une fois les deux calques de l'enveloppe affichés */
      const images = [...e.target.querySelectorAll('img')];
      Promise.all(images.map((i) => (i.complete ? Promise.resolve() : i.decode().catch(() => {})))).then(() => {
        e.target.classList.remove('rdp-enveloppe--a-sortir');
      });
    });
  }, { threshold: 0.35 });
  document.querySelectorAll('[data-rdp-enveloppe]').forEach((env) => {
    if (env.dataset.rdpPret) return;
    env.dataset.rdpPret = '';
    /* déjà visible au chargement (ex. retour après envoi du formulaire) : pas d'animation */
    const r = env.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.6 && r.bottom > 0) return;
    env.classList.add('rdp-enveloppe--a-sortir');
    obs.observe(env);
  });
}
enveloppes();
document.addEventListener('shopify:section:load', enveloppes);

/* Bandeaux défilants : le motif est répété jusqu'à couvrir au moins une largeur d'écran,
   puis cette moitié est doublée. L'animation va de 0 à -50 % : la seconde moitié prend
   exactement la place de la première, sans trou ni saut. Vitesse constante (px/s). */
function bandeauxDefilants(racine = document) {
  racine.querySelectorAll('[data-rdp-piste-defilante]').forEach((piste) => {
    const motif = piste.querySelector('[data-rdp-motif]');
    if (!motif) return;
    const construire = () => {
      piste.querySelectorAll('[data-rdp-copie]').forEach((c) => c.remove());
      const largeurMotif = motif.getBoundingClientRect().width;
      if (!largeurMotif) return;
      const largeurVue = piste.parentElement.getBoundingClientRect().width || window.innerWidth;
      const parMoitie = Math.max(1, Math.ceil(largeurVue / largeurMotif));
      for (let i = 1; i < parMoitie * 2; i++) {
        const copie = motif.cloneNode(true);
        copie.removeAttribute('data-rdp-motif');
        copie.setAttribute('data-rdp-copie', '');
        copie.setAttribute('aria-hidden', 'true');
        piste.append(copie);
      }
      const vitesse = Number(piste.dataset.rdpVitesse) || 70; /* px par seconde */
      piste.style.setProperty('--rdp-duree-defile', `${((largeurMotif * parMoitie) / vitesse).toFixed(2)}s`);
    };
    construire();
    if (document.fonts?.ready) document.fonts.ready.then(construire);
    let minuterie;
    window.addEventListener('resize', () => { clearTimeout(minuterie); minuterie = setTimeout(construire, 200); });
  });
}
bandeauxDefilants();
document.addEventListener('shopify:section:load', (e) => bandeauxDefilants(e.target));

/* Galerie d'édition : un clic sur une photo ouvre la visionneuse plein écran
   (flèches, points, miniature au-dessus du point actif, clavier, Échap). */
class RdpGalerie extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-rdp-visionneuse]');
    if (!this.dialog) return;
    this.vues = [...this.dialog.querySelectorAll('[data-rdp-vue]')];
    this.points = [...this.dialog.querySelectorAll('[data-rdp-vue-point]')];
    this.vignette = this.dialog.querySelector('[data-rdp-vue-vignette]');
    this.querySelectorAll('[data-rdp-ouvrir]').forEach((b) => b.addEventListener('click', () => this.ouvrir(Number(b.dataset.rdpOuvrir))));
    this.dialog.querySelector('[data-rdp-visionneuse-fermer]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('[data-rdp-vue-prec]')?.addEventListener('click', () => this.montrer(this.i - 1));
    this.dialog.querySelector('[data-rdp-vue-suiv]')?.addEventListener('click', () => this.montrer(this.i + 1));
    this.points.forEach((p) => p.addEventListener('click', () => this.montrer(Number(p.dataset.rdpVuePoint))));
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.montrer(this.i + 1);
      if (e.key === 'ArrowLeft') this.montrer(this.i - 1);
    });
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('rdp-infos-ouvert');
      this.querySelector('rdp-bande')?.aller?.(this.i);
    });
  }

  ouvrir(i) {
    this.dialog.showModal();
    document.documentElement.classList.add('rdp-infos-ouvert');
    this.montrer(i);
  }

  montrer(i) {
    const n = this.vues.length;
    this.i = (i + n) % n;
    this.vues.forEach((v, k) => { v.hidden = k !== this.i; });
    this.points.forEach((p, k) => {
      p.toggleAttribute('aria-current', k === this.i);
      const d = Math.abs(k - this.i);
      p.dataset.rdpDistance = d >= 5 ? 'loin' : d >= 4 ? 'moyen' : '';
    });
    const img = this.vues[this.i].querySelector('img');
    const point = this.points[this.i];
    if (this.vignette && img && point) {
      this.vignette.innerHTML = '';
      const mini = document.createElement('img');
      mini.src = img.currentSrc || img.src;
      mini.alt = '';
      mini.className = 'rdp-points__vignette-image est-visible';
      this.vignette.append(mini);
      this.vignette.style.setProperty('--rdp-vignette-x', `${point.offsetLeft + point.offsetWidth / 2}px`);
    }
  }
}
customElements.get('rdp-galerie') || customElements.define('rdp-galerie', RdpGalerie);

/* Bande photo (galeries d'édition et d'événement, maquette « CDR-editions-passees ») :
   la photo en cours est en grand, calée après un aperçu de la précédente ; les suivantes,
   plus petites, filent vers la droite et peuvent être coupées par le bord de l'écran.
   Flèches, points, miniature au-dessus du point actif, clavier et glisser au doigt. */
class RdpBande extends HTMLElement {
  connectedCallback() {
    this.piste = this.querySelector('[data-rdp-bande-piste]');
    if (!this.piste) return;
    this.elements = [...this.piste.children];
    this.points = [...this.querySelectorAll('[data-rdp-bande-point]')];
    this.vignette = this.querySelector('[data-rdp-bande-vignette]');
    this.barre = this.querySelector('[data-rdp-bande-barre]');
    this.i = 0;
    this.querySelector('[data-rdp-bande-prec]')?.addEventListener('click', () => this.aller(this.i - 1));
    this.querySelector('[data-rdp-bande-suiv]')?.addEventListener('click', () => this.aller(this.i + 1));
    this.points.forEach((p) => p.addEventListener('click', () => this.aller(Number(p.dataset.rdpBandePoint))));
    /* un clic sur une petite photo l'amène au premier plan (la grande ouvre la visionneuse) */
    this.elements.forEach((el, n) => el.addEventListener('click', (e) => {
      if (this.glisse) return;
      if (n !== this.i) { e.preventDefault(); e.stopImmediatePropagation(); this.aller(n); }
    }, true));
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.aller(this.i + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.aller(this.i - 1); }
    });
    /* glisser à la souris ou au doigt : la piste suit le pointeur, puis se cale sur la photo la plus proche */
    let x0 = null;
    let ecart = 0;
    this.piste.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      x0 = e.clientX; ecart = 0; this.glisse = false;
    });
    this.piste.addEventListener('pointermove', (e) => {
      if (x0 === null) return;
      ecart = e.clientX - x0;
      if (!this.glisse && Math.abs(ecart) > 6) {
        this.glisse = true;
        this.piste.classList.add('est-glisse');
        /* capture seulement une fois le glissé commencé : un simple clic garde sa cible */
        this.piste.setPointerCapture(e.pointerId);
      }
      if (this.glisse) {
        /* résistance aux deux bouts */
        const bout = (this.i === 0 && ecart > 0) || (this.i === this.elements.length - 1 && ecart < 0);
        this.piste.style.transform = `translateX(${(this.decalage + (bout ? ecart / 3 : ecart)).toFixed(1)}px)`;
      }
    });
    const lacher = () => {
      if (x0 === null) return;
      x0 = null;
      this.piste.classList.remove('est-glisse');
      if (!this.glisse) return;
      const pas = this.pas || 1;
      const sauts = Math.abs(ecart) > 40 ? Math.max(1, Math.round(Math.abs(ecart) / pas)) : 0;
      this.aller(this.i + (ecart < 0 ? sauts : -sauts));
      /* le clic qui suit un glissé ne doit pas ouvrir la visionneuse */
      setTimeout(() => { this.glisse = false; }, 0);
    };
    this.piste.addEventListener('pointerup', lacher);
    this.piste.addEventListener('pointercancel', lacher);
    this.piste.addEventListener('click', (e) => { if (this.glisse) { e.preventDefault(); e.stopImmediatePropagation(); } }, true);
    this.piste.addEventListener('dragstart', (e) => e.preventDefault());
    new ResizeObserver(() => this.placer()).observe(this);
    this.aller(0);
  }

  aller(i) {
    const n = this.elements.length;
    this.i = Math.max(0, Math.min(i, n - 1));
    this.elements.forEach((el, k) => el.classList.toggle('est-courant', k === this.i));
    this.points.forEach((p, k) => {
      p.toggleAttribute('aria-current', k === this.i);
      const d = Math.abs(k - this.i);
      p.dataset.rdpDistance = d >= 5 ? 'loin' : d >= 4 ? 'moyen' : '';
    });
    const prec = this.querySelector('[data-rdp-bande-prec]');
    const suiv = this.querySelector('[data-rdp-bande-suiv]');
    if (prec) prec.disabled = this.i === 0;
    if (suiv) suiv.disabled = this.i === n - 1;
    if (this.barre) {
      this.barre.style.width = `${100 / n}%`;
      this.barre.style.transform = `translateX(${this.i * 100}%)`;
    }
    if (this.vignette && this.points[this.i]) {
      const p = this.points[this.i];
      this.vignette.style.setProperty('--rdp-vignette-x', `${p.offsetLeft + p.offsetWidth / 2}px`);
      this.vignette.querySelectorAll('[data-rdp-vignette-image]').forEach((img) => img.classList.toggle('est-visible', Number(img.dataset.rdpVignetteImage) === this.i));
    }
    this.placer();
    this.dispatchEvent(new CustomEvent('rdp:bande', { detail: { i: this.i }, bubbles: true }));
  }

  /* décale la piste pour que la photo en cours commence au repère (--rdp-bande-depart) :
     toutes les photos avant elle sont petites ; on mesure une petite photo qui ne change pas de taille */
  placer() {
    const n = this.elements.length;
    if (!n) return;
    const temoin = this.elements.find((e, k) => k !== this.i && k !== this.precedent) || this.elements.find((e, k) => k !== this.i);
    const petite = temoin ? temoin.offsetWidth : 0;
    const gap = parseFloat(getComputedStyle(this.piste).columnGap) || 0;
    const sonde = document.createElement('div');
    sonde.style.cssText = 'position:absolute;visibility:hidden;width:var(--rdp-bande-depart)';
    this.append(sonde);
    const depart = sonde.offsetWidth;
    sonde.remove();
    const x = this.i * (petite + gap);
    this.pas = petite + gap;
    this.decalage = depart - x;
    this.piste.style.transform = `translateX(${this.decalage.toFixed(1)}px)`;
    this.precedent = this.i;
  }
}
customElements.get('rdp-bande') || customElements.define('rdp-bande', RdpBande);

/* la visionneuse reprend la photo en cours de la bande, et la bande suit la visionneuse */
document.addEventListener('rdp:bande', (e) => {
  const galerie = e.target.closest('rdp-galerie');
  if (galerie) galerie.iBande = e.detail.i;
});

/* Boutique fermée (réglage du thème « Boutique ouverte » décoché) : ses liens restent visibles, grisés par le CSS, mais ne mènent nulle part */
(() => {
  if (!document.body.classList.contains('rdp-boutique-fermee')) return;
  const estBoutique = (a) => /\/collections\/(?!billetterie)/.test(a.getAttribute('href') || '');
  const neutraliser = () => document.querySelectorAll('a[href*="/collections/"]').forEach((a) => {
    if (!estBoutique(a)) return;
    a.setAttribute('aria-disabled', 'true');
    a.tabIndex = -1;
  });
  neutraliser();
  document.addEventListener('DOMContentLoaded', neutraliser);
  document.addEventListener('click', (e) => {
    const a = e.target.closest?.('a[href*="/collections/"]');
    if (a && estBoutique(a)) e.preventDefault();
  }, true);
})();

/* Listes à faire glisser à la souris (data-rdp-glisser) : au doigt, le défilement natif suffit. */
function glisserListes(racine = document) {
  racine.querySelectorAll('[data-rdp-glisser]').forEach((liste) => {
    if (liste.dataset.rdpGlisserPret) return;
    liste.dataset.rdpGlisserPret = '1';
    let x0 = 0, depart = 0, actif = false, bouge = false;
    liste.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || liste.scrollWidth <= liste.clientWidth) return;
      actif = true; bouge = false; x0 = e.clientX; depart = liste.scrollLeft;
      liste.setPointerCapture(e.pointerId);
    });
    liste.addEventListener('pointermove', (e) => {
      if (!actif) return;
      const dx = e.clientX - x0;
      if (Math.abs(dx) > 3) { bouge = true; liste.classList.add('est-glisse'); }
      liste.scrollLeft = depart - dx;
    });
    const fin = () => { actif = false; liste.classList.remove('est-glisse'); };
    liste.addEventListener('pointerup', fin);
    liste.addEventListener('pointercancel', fin);
    liste.addEventListener('click', (e) => { if (bouge) { e.preventDefault(); e.stopPropagation(); bouge = false; } }, true);
  });
}
glisserListes();
document.addEventListener('shopify:section:load', (e) => glisserListes(e.target));
